import { Component, Inject, Input, LOCALE_ID, OnChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AssetGroupDto } from '@assetaccountant/api/models/asset-group.dto';
import { AssetTransaction } from '@assetaccountant/assets/models/asset-transaction-api-model';
import {
  getTransactionDescription,
  getTransactionSource,
  transactionHasTaxableUse,
} from '@assetaccountant/assets/models/asset-transaction.utils';
import { RegisterData, RegisterParams } from '@assetaccountant/core/resolvers/register.resolver';
import { RouteService } from '@assetaccountant/core/services/route.service';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';
import { slug } from '@assetaccountant/core/utils/slug';

type AssetTransactionAdjusted = AssetTransaction & {
  showResidualValue?: boolean;
};

export abstract class TransactionDescriptionBase {
  abstract data: AssetTransactionAdjusted;
  abstract assetGroups?: AssetGroupDto[];

  type?: string;
  hasTaxableUse: boolean;
  formattedDescription: string;
  formattedNotes: string;
  importName?: string | null;
  sourceId?: string;
  sourceLink = '';
  sourceText?: string;

  isMultiLine: boolean;

  getTransactionDescription = getTransactionDescription(this.locale, this.route);

  constructor(
    private locale: string,
    private route: TypedActivatedRoute<RegisterParams, RegisterData>,
    private routeService: RouteService,
  ) {}

  update() {
    if (this.data) {
      this.type = this.data.type;
      this.hasTaxableUse = transactionHasTaxableUse(this.data);

      // Support old (\\n) and new (\n)
      this.formattedDescription = this.getTransactionDescription(
        this.data,
        this.assetGroups,
      ).replace(/\n|\\n/g, '<br/>');
      this.formattedNotes = this.data.notes?.replace(/\n|\\n/g, '<br/>') || '';
      [this.sourceText, this.importName] = getTransactionSource(this.data);
      this.sourceLink = `${this.routeService.getLinkWithReplacedParameters([
        'organisations',
        'registers',
      ])}/imports`;
      if (this.data.sourceId) {
        this.sourceLink = `${this.sourceLink}/${this.data.sourceId}`;
      }

      this.isMultiLine = !!(
        this.formattedDescription &&
        (this.formattedDescription.includes('<br/>') || this.sourceText || this.importName)
      );
    } else {
      this.type = undefined;
      this.hasTaxableUse = false;
      this.formattedDescription = '';
      this.formattedNotes = '';
      this.sourceText = '';
      this.importName = null;
      this.sourceLink = '';
      this.isMultiLine = false;
    }
  }

  assetName(data: AssetTransactionAdjusted, prefix: 'component' | 'parent') {
    const code = data[prefix + 'Code'];
    const name = data[prefix + 'Name'];
    return code ? `${code} ${name}` : (name ?? '(Deleted)');
  }

  assetRoute(data: AssetTransactionAdjusted, prefix: 'component' | 'parent') {
    const name = data[prefix + 'Name'];
    return name ? ['..', `${data[prefix + 'Id']}-${slug(name)}`] : null;
  }

  assetGroupName(data: AssetTransactionAdjusted) {
    const assetGroup = this.assetGroups?.find(ag => ag.assetGroupId === data.assetGroupId);
    let assetGroupName = assetGroup?.name;
    if (assetGroup?.isActive === false) {
      assetGroupName = assetGroupName + ' (inactive)';
    }
    return assetGroupName ?? data.assetGroupId;
  }

  assetGroupRoute(data: AssetTransactionAdjusted) {
    return this.routeService.getAssetGroupRoute(data.assetGroupId);
  }

  poolRoute({ poolCode }: AssetTransactionAdjusted) {
    return ['../../../../years/-/pools/', poolCode];
  }
}

@Component({
  selector: 'app-transaction-description',
  templateUrl: './transaction-description-cell-renderer.component.html',
  styleUrls: ['./transaction-description-cell-renderer.component.scss'],
  standalone: false,
})
export class TransactionDescriptionComponent
  extends TransactionDescriptionBase
  implements OnChanges
{
  @Input() data: AssetTransactionAdjusted;
  @Input() assetGroups?: AssetGroupDto[];

  constructor(
    @Inject(LOCALE_ID) locale: string,
    @Inject(ActivatedRoute) route: TypedActivatedRoute<RegisterParams, RegisterData>,
    routeService: RouteService,
  ) {
    super(locale, route, routeService);
  }

  ngOnChanges() {
    this.update();
  }
}
